import * as React from 'react';
import { Order, SymbolData } from '../../reducers/symbolSlice';
import { AdvancedChartComponent } from '../../components/advanced-chart';
import { getFormattedDate, getInDoller } from '../../utils/utils';
import './style.scss';
import { AlpacaPosition } from '../../reducers/positionSlice';
import { TableComponent } from '../../components/table';
import { TechnicalMetrics } from '../../utils/technicalAnalysis';
import { OptionsChainChart, PastOptionsStrategyDashboard, NextOptionsStrategyDashboard } from '../../components/options-chain';
import { OptionChainData, OptionsPrediction } from '../../reducers/optionsSlice';
import { Bar } from '../../reducers/symbolSlice';
import { RecommendationDetailModal } from '../../components/recommendation-detail-modal';
import { PastExpirationAnalysis, StrikeHighlight, aggregatePastPeriodChains, buildNextPeriodStrikeHighlightsFromPastVolume, generateOptionsPrediction } from '../../utils/optionsService';

const countStrikeHighlights = (highlights: Record<number, StrikeHighlight>) => {
    let callWins = 0;
    let putWins = 0;
    let predictedCallStrikes = 0;
    let predictedPutStrikes = 0;

    Object.values(highlights).forEach((highlight) => {
        if (highlight.call === 'success') {
            callWins += 1;
        }
        if (highlight.put === 'success') {
            putWins += 1;
        }
        if (highlight.callPredicted) {
            predictedCallStrikes += 1;
        }
        if (highlight.putPredicted) {
            predictedPutStrikes += 1;
        }
    });

    return { callWins, putWins, predictedCallStrikes, predictedPutStrikes };
};

const getChainVolumes = (chainData: OptionChainData) => {
    const callVolume = chainData.calls.reduce((sum, call) => sum + call.volume, 0);
    const putVolume = chainData.puts.reduce((sum, put) => sum + put.volume, 0);
    const volumeRatio = callVolume > 0 ? putVolume / callVolume : 0;

    return { callVolume, putVolume, volumeRatio };
};

interface SymbolPageProps {
    data: SymbolData;
    symbol: string;
    chartPeriod: string;
    profitLoss: number;

    position?: AlpacaPosition;
	updateChart: (period: string) => void;
    technicalMetrics: TechnicalMetrics | null;
    optionChainData?: OptionChainData | null;
    optionsPrediction?: OptionsPrediction | null;
    expirationDates?: string[];
    selectedExpirationDate?: string;
    onExpirationDateChange?: (expirationDate: string) => void;
    pastThreeMonthBars?: Bar[];
    strategyHistory?: PastExpirationAnalysis[];
    strategyHistoryLoading?: boolean;
    marketDate?: string;
}

const recommendationLabels: Record<TechnicalMetrics['recommendation'], string> = {
    strong_buy: 'Strong Buy',
    buy: 'Buy',
    hold: 'Hold',
    sell: 'Sell',
    strong_sell: 'Strong Sell',
};

export const SymbolPage: React.FC<SymbolPageProps> = ({ data, symbol, chartPeriod, position, profitLoss, updateChart, technicalMetrics, optionChainData, optionsPrediction, expirationDates = [], selectedExpirationDate = 'all', onExpirationDateChange, pastThreeMonthBars = [], strategyHistory = [], strategyHistoryLoading = false, marketDate = '' }) => {
    const [showAdvanced, setShowAdvanced] = React.useState(false);
    const [showRecommendationModal, setShowRecommendationModal] = React.useState(false);
    const [selectedPastExpirationDate, setSelectedPastExpirationDate] = React.useState('');
    const pastExpirationAnalyses = strategyHistory;
    const pastExpirationDates = React.useMemo(
        () => pastExpirationAnalyses.map((analysis) => analysis.expirationDate),
        [pastExpirationAnalyses]
    );
    const currentPrice = data.bars && data.bars.length > 0 ? data.bars[data.bars.length - 1].c : 0;
    const pastAggregatedChain = React.useMemo(
        () => aggregatePastPeriodChains(pastExpirationAnalyses),
        [pastExpirationAnalyses]
    );
    const pastBasedPrediction = React.useMemo(() => {
        const hasPastVolume = pastAggregatedChain.calls.some((call) => call.volume > 0)
            || pastAggregatedChain.puts.some((put) => put.volume > 0);

        if (!hasPastVolume) {
            return null;
        }

        return generateOptionsPrediction(pastAggregatedChain, currentPrice);
    }, [currentPrice, pastAggregatedChain]);
    const nextPeriodHighlights = React.useMemo(() => {
        if (!optionChainData || !pastBasedPrediction) {
            return {};
        }

        return buildNextPeriodStrikeHighlightsFromPastVolume(
            optionChainData,
            pastAggregatedChain,
            pastBasedPrediction
        );
    }, [optionChainData, pastAggregatedChain, pastBasedPrediction]);
    const futurePeriodEndDate = React.useMemo(() => {
        if (selectedExpirationDate !== 'all') {
            return selectedExpirationDate;
        }

        return expirationDates.length > 0 ? expirationDates[expirationDates.length - 1] : marketDate;
    }, [expirationDates, marketDate, selectedExpirationDate]);

    React.useEffect(() => {
        if (!pastExpirationDates.length) {
            setSelectedPastExpirationDate('');
            return;
        }

        if (!pastExpirationDates.includes(selectedPastExpirationDate)) {
            setSelectedPastExpirationDate(pastExpirationDates[pastExpirationDates.length - 1]);
        }
    }, [pastExpirationDates, selectedPastExpirationDate]);

    const selectedPastAnalysis = React.useMemo(
        () => pastExpirationAnalyses.find((analysis) => analysis.expirationDate === selectedPastExpirationDate)
            || pastExpirationAnalyses[pastExpirationAnalyses.length - 1]
            || null,
        [pastExpirationAnalyses, selectedPastExpirationDate]
    );

    const pastStrategySummary = React.useMemo(() => {
        if (!selectedPastAnalysis) {
            return null;
        }

        const { callVolume, putVolume, volumeRatio } = getChainVolumes(selectedPastAnalysis.chainData);
        const { callWins, putWins } = countStrikeHighlights(selectedPastAnalysis.strikeHighlights);

        return {
            expirationDate: selectedPastAnalysis.expirationDate,
            startDate: selectedPastAnalysis.startDate,
            endDate: selectedPastAnalysis.endDate,
            startPrice: selectedPastAnalysis.startPrice,
            endPrice: selectedPastAnalysis.endPrice,
            priceChangePercent: selectedPastAnalysis.priceChangePercent,
            callVolume,
            putVolume,
            volumeRatio,
            callWins,
            putWins,
        };
    }, [selectedPastAnalysis]);

    const futureStrategySummary = React.useMemo(() => {
        if (!pastBasedPrediction) {
            return null;
        }

        const { predictedCallStrikes, predictedPutStrikes } = countStrikeHighlights(nextPeriodHighlights);
        const pastPeriodStart = pastExpirationAnalyses[0]?.startDate || marketDate;
        const pastPeriodEnd = pastExpirationAnalyses[pastExpirationAnalyses.length - 1]?.endDate || marketDate;

        return {
            currentPrice,
            startDate: marketDate,
            endDate: futurePeriodEndDate,
            pastVolumeStartDate: pastPeriodStart,
            pastVolumeEndDate: pastPeriodEnd,
            prediction: pastBasedPrediction,
            predictedCallStrikes,
            predictedPutStrikes,
            basedOnPastVolume: true,
        };
    }, [currentPrice, futurePeriodEndDate, marketDate, nextPeriodHighlights, pastBasedPrediction, pastExpirationAnalyses]);

    const columns = [{
        key: 'side',
		label: 'Order Type',
    }, {
        key: 'filled_qty',
		label: 'Qty',
    }, {
        key: 'filled_avg_price',
		label: 'Order Price',
        renderer: (order: Order) => {
            return `$${order.filled_avg_price}`;
        } 
    }, {
        key: 'total_amount',
        label: 'Total Amount',
        renderer: (order: Order) => {
            return `$${(parseFloat(order.filled_qty) * parseFloat(order.filled_avg_price)).toFixed(2)}`;
        }
    }, {
        key: 'total_amount',
        label: 'Submitted Date',
        renderer: (order: Order) => {
            return getFormattedDate(new Date(order.submitted_at));
        }
    }];

    const previousPrice = data.bars && data.bars.length > 0 ? data.bars[0].c : 0;
    const priceChange = currentPrice - previousPrice;
    const priceChangePercent = previousPrice > 0 ? ((priceChange / previousPrice) * 100).toFixed(2) : '0.00';

    const smaState = technicalMetrics ? (technicalMetrics.sma.deltaPercent >= 0 ? 'bullish' : 'bearish') : 'neutral';
    const emaState = technicalMetrics ? (technicalMetrics.ema.deltaPercent >= 0 ? 'bullish' : 'bearish') : 'neutral';
    const macdState = technicalMetrics ? technicalMetrics.macd.trend : 'neutral';
    const volumeState = technicalMetrics ? (technicalMetrics.volume.isHigh ? 'high' : 'normal') : 'normal';
    const atrState = technicalMetrics
        ? (technicalMetrics.trailingStopPercent >= 15 ? 'high' : technicalMetrics.trailingStopPercent <= 8 ? 'low' : 'normal')
        : 'normal';
    const volumeStateLabel = volumeState === 'high' ? 'high activity' : 'normal activity';
    const atrStateLabel = atrState === 'high' ? 'high volatility' : atrState === 'low' ? 'low volatility' : 'normal volatility';

    return (
		<div className="symbolPage">
            {/* Symbol Dashboard - Top */}
            <div className='symbolDashboard'>
                <div className='dashboard-header'>
                    <h2>{symbol}</h2>
                </div>

                <div className='metrics-grid'>
                    {/* Current Price */}
                    <div className='metric-card'>
                        <div className='metric-label'>Current Price</div>
                        <div className='metric-value'>${currentPrice.toFixed(2)}</div>
                        <div className={`metric-subtext ${priceChange >= 0 ? 'positive' : 'negative'}`}>
                            {priceChange >= 0 ? '↑' : '↓'} ${Math.abs(priceChange).toFixed(2)} ({priceChangePercent}%)
                        </div>
                    </div>

                    {/* Quantity Held */}
                    {position && (
                        <div className='metric-card'>
                            <div className='metric-label'>Quantity Held</div>
                            <div className='metric-value'>{position.qty}</div>
                            <div className='metric-subtext'>Open positions</div>
                        </div>
                    )}

                    {/* Buy Price */}
                    {position && (
                        <div className='metric-card'>
                            <div className='metric-label'>Buy Price</div>
                            <div className='metric-value'>${position.avg_entry_price}</div>
                            <div className='metric-subtext'>Average entry</div>
                        </div>
                    )}

                    {/* Change Today */}
                    {position && (
                        <div className='metric-card'>
                            <div className='metric-label'>Change Today</div>
                            <div className={`metric-value ${parseFloat(position.change_today) >= 0 ? 'positive' : 'negative'}`}>
                                {parseFloat(position.change_today) > 0 ? '↑' : '↓'} {`${(parseFloat(position.change_today) * 100).toFixed(2)}%`}
                            </div>
                            <div className='metric-subtext'>Daily performance</div>
                        </div>
                    )}

                    {/* Overall Change */}
                    {position && (
                        <div className='metric-card'>
                            <div className='metric-label'>Overall Change</div>
                            <div className={`metric-value ${parseFloat(position.unrealized_plpc) >= 0 ? 'positive' : 'negative'}`}>
                                {parseFloat(position.unrealized_plpc) > 0 ? '↑' : '↓'} {`${(parseFloat(position.unrealized_plpc) * 100).toFixed(2)}%`}
                            </div>
                            <div className='metric-subtext'>Total return</div>
                        </div>
                    )}

                    {/* Profit/Loss */}
                    {profitLoss !== undefined && (
                        <div className='metric-card'>
                            <div className='metric-label'>Profit/Loss</div>
                            <div className={`metric-value ${profitLoss >= 0 ? 'positive' : 'negative'}`}>
                                {getInDoller(profitLoss)}
                            </div>
                            <div className='metric-subtext'>Unrealized P&L</div>
                        </div>
                    )}


                </div>

                {technicalMetrics && (
                    <>
                        <div className='technical-header'>
                            <h3>Technical Analysis</h3>
                            <span>Portfolio Model (50/200/14)</span>
                        </div>
                        <div className='technical-grid'>
                            <div className='metric-card'>
                                <div className='metric-label' title='Momentum oscillator from 0 to 100. Below 30 can indicate oversold; above 70 can indicate overbought.'>RSI {technicalMetrics.rsi.period}</div>
                                <div className='metric-value'>{technicalMetrics.rsi.value.toFixed(2)}</div>
                                <div className='metric-subtext'>
                                    <span className={`state-badge ${technicalMetrics.rsi.level}`}>
                                        {technicalMetrics.rsi.level}
                                    </span>
                                </div>
                            </div>
                            <div className='metric-card'>
                                <div className='metric-label' title='Compares fast and slow momentum. Bullish when MACD is above its signal line.'>MACD Trend</div>
                                <div className={`metric-value ${technicalMetrics.macd.trend === 'bullish' ? 'positive' : technicalMetrics.macd.trend === 'bearish' ? 'negative' : ''}`}>
                                    {technicalMetrics.macd.histogram.toFixed(3)}
                                </div>
                                <div className='metric-subtext'>
                                    Signal {technicalMetrics.macd.signal.toFixed(3)}
                                    <span className={`state-badge ${macdState}`}>{macdState}</span>
                                </div>
                            </div>
                            <div className='metric-card'>
                                <div className='metric-label' title='Simple moving average gives a smoother trend baseline and dynamic support/resistance context.'>SMA {technicalMetrics.sma.period}</div>
                                <div className='metric-value'>${technicalMetrics.sma.value.toFixed(2)}</div>
                                <div className={`metric-subtext ${technicalMetrics.sma.deltaPercent >= 0 ? 'positive' : 'negative'}`}>
                                    {technicalMetrics.sma.deltaPercent >= 0 ? '↑' : '↓'} {Math.abs(technicalMetrics.sma.deltaPercent).toFixed(2)}% vs price
                                    <span className={`state-badge ${smaState}`}>{smaState}</span>
                                </div>
                            </div>
                            <div className='metric-card'>
                                <div className='metric-label' title='Exponential moving average reacts faster to recent price changes than SMA.'>EMA {technicalMetrics.ema.period}</div>
                                <div className='metric-value'>${technicalMetrics.ema.value.toFixed(2)}</div>
                                <div className={`metric-subtext ${technicalMetrics.ema.deltaPercent >= 0 ? 'positive' : 'negative'}`}>
                                    {technicalMetrics.ema.deltaPercent >= 0 ? '↑' : '↓'} {Math.abs(technicalMetrics.ema.deltaPercent).toFixed(2)}% vs price
                                    <span className={`state-badge ${emaState}`}>{emaState}</span>
                                </div>
                            </div>
                            <div className='metric-card'>
                                <div className='metric-label' title='Trading activity compares current traded shares with average traded shares.'>Trading Activity</div>
                                <div className='metric-value'>{technicalMetrics.volume.current.toLocaleString()}</div>
                                <div className={`metric-subtext ${technicalMetrics.volume.isHigh ? 'positive' : ''}`}>
                                    Avg {technicalMetrics.volume.average.toLocaleString()}
                                    <span className={`state-badge ${volumeState}`}>{volumeStateLabel}</span>
                                </div>
                            </div>
                            <div className='metric-card'>
                                <div className='metric-label' title='Price volatility from Average True Range. It measures how far price moves, not how many shares are traded.'>Price Volatility (ATR 14)</div>
                                <div className='metric-value'>${technicalMetrics.atr.toFixed(2)}</div>
                                <div className='metric-subtext'>
                                    Average true range volatility
                                    <span className={`state-badge ${atrState}`}>{atrStateLabel}</span>
                                </div>
                            </div>
                            <div className='metric-card'>
                                <div className='metric-label' title='Dynamic trailing stop scales with volatility to avoid exits that are too tight in choppy markets.'>Dynamic Trailing Stop</div>
                                <div className='metric-value'>{technicalMetrics.trailingStopPercent.toFixed(2)}%</div>
                                <div className='metric-subtext'>Volatility adjusted exit buffer</div>
                            </div>
                            <div className='metric-card recommendation-card'>
                                <div className='metric-label'>Recommendation</div>
                                <div className={`metric-value ${technicalMetrics.recommendation.includes('buy') ? 'positive' : technicalMetrics.recommendation.includes('sell') ? 'negative' : ''}`}>
                                    {recommendationLabels[technicalMetrics.recommendation]}
                                </div>
                                <div className='metric-subtext'>
                                    Net {technicalMetrics.netScore >= 0 ? '+' : ''}{technicalMetrics.netScore}
                                    {' '}(Bull {technicalMetrics.bullishScore} · Bear {technicalMetrics.bearishScore})
                                </div>
                                <button
                                    type='button'
                                    className='recommendation-details-link'
                                    onClick={() => setShowRecommendationModal(true)}
                                >
                                    View recommendation details
                                </button>
                            </div>
                        </div>

                        {showRecommendationModal && (
                            <RecommendationDetailModal
                                metrics={technicalMetrics}
                                onClose={() => setShowRecommendationModal(false)}
                            />
                        )}

                        <div className='advanced-toggle-row'>
                            <button className='advanced-toggle' onClick={() => setShowAdvanced(!showAdvanced)}>
                                {showAdvanced ? 'Hide Advanced Metrics' : 'Show Advanced Metrics'}
                            </button>
                        </div>

                        {showAdvanced && (
                            <div className='technical-grid advanced-grid'>
                                <div className='metric-card'>
                                    <div className='metric-label' title='Band width expands when volatility rises and contracts during consolidations.'>Bollinger Width</div>
                                    <div className='metric-value'>{technicalMetrics.bollinger.widthPercent.toFixed(2)}%</div>
                                    <div className='metric-subtext'>20-period volatility band</div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Chart and Orders Section - Side by side */}
            <div className='contentSection'>
                {/* Chart on Left */}
                <div className='chartSection'>
                    <div className='chart-header'>
                        <h3>Price Chart</h3>
                        <div className='period-selector'>
                            <button 
                                className={chartPeriod === '1D' ? 'active' : ''} 
                                onClick={() => updateChart('1D')}
                            >1D</button>
                            <button 
                                className={chartPeriod === '1W' ? 'active' : ''} 
                                onClick={() => updateChart('1W')}
                            >1W</button>
                            <button 
                                className={chartPeriod === '1M' ? 'active' : ''} 
                                onClick={() => updateChart('1M')}
                            >1M</button>
                            <button 
                                className={chartPeriod === '3M' ? 'active' : ''} 
                                onClick={() => updateChart('3M')}
                            >3M</button>
                            <button 
                                className={chartPeriod === 'YTD' ? 'active' : ''} 
                                onClick={() => updateChart('YTD')}
                            >YTD</button>
                            <button 
                                className={chartPeriod === '1Y' ? 'active' : ''} 
                                onClick={() => updateChart('1Y')}
                            >1Y</button>
                        </div>
                    </div>
                    <AdvancedChartComponent 
                        symbol={symbol} 
                        bars={data.bars} 
                        currentPeriod={chartPeriod}
                        updateChart={updateChart}
                    />
                </div>

                {/* Orders on Right */}
                <div className='ordersSection'>
                    <TableComponent 
                        data={data.orders} 
                        columns={columns} 
                        itemsPerPage={15}
                        title={
                            <div className='section-header'>
                                <h3>All Orders</h3>
                                <span className='order-count'>({data.orders.length} orders)</span>
                            </div>
                        }
                    />
                </div>
            </div>

            {/* Options Analysis Section */}
            {(optionChainData || pastExpirationAnalyses.length > 0 || strategyHistoryLoading) && (
                <div className='optionsSection'>
                    {(strategyHistoryLoading || pastExpirationAnalyses.length > 0) && pastThreeMonthBars.length > 1 && (
                        <div className='past-options-section'>
                            <div className='options-section-heading'>
                                <h3>Past 3 Months Strategy</h3>
                                <span>Select an expiry to review volume and ITM wins</span>
                            </div>
                            <PastOptionsStrategyDashboard
                                summary={pastStrategySummary}
                                loading={strategyHistoryLoading && pastExpirationAnalyses.length === 0}
                            />
                            {strategyHistoryLoading && (
                                <OptionsChainChart
                                    chainData={null}
                                    currentPrice={pastThreeMonthBars[0].c}
                                    title="Options Chain Volume Analysis — Past 3 Months"
                                    mode="past"
                                    loading={true}
                                />
                            )}
                            {!strategyHistoryLoading && selectedPastAnalysis && (
                                <OptionsChainChart
                                    key={selectedPastAnalysis.expirationDate}
                                    chainData={selectedPastAnalysis.chainData}
                                    currentPrice={selectedPastAnalysis.startPrice}
                                    endPrice={selectedPastAnalysis.endPrice}
                                    priceChangePercent={selectedPastAnalysis.priceChangePercent}
                                    periodStartDate={selectedPastAnalysis.startDate}
                                    periodEndDate={selectedPastAnalysis.endDate}
                                    title={`Options Chain Volume Analysis — Expiry ${selectedPastAnalysis.expirationDate}`}
                                    strikeHighlights={selectedPastAnalysis.strikeHighlights}
                                    expirationDates={pastExpirationDates}
                                    selectedExpirationDate={selectedPastExpirationDate}
                                    onExpirationDateChange={setSelectedPastExpirationDate}
                                    mode="past"
                                />
                            )}
                            {!strategyHistoryLoading && pastExpirationAnalyses.length === 0 && (
                                <div className='options-empty-note'>No past expiry options volume found for the last 3 months.</div>
                            )}
                        </div>
                    )}

                    {optionChainData && pastBasedPrediction && (
                        <div className='future-options-section'>
                            <div className='options-section-heading'>
                                <h3>Next 3 Months Strategy</h3>
                                <span>Predictions based on past 3 months call/put volume</span>
                            </div>
                            {futureStrategySummary && (
                                <NextOptionsStrategyDashboard summary={futureStrategySummary} />
                            )}
                            <OptionsChainChart
                                chainData={optionChainData}
                                currentPrice={currentPrice}
                                periodStartDate={marketDate}
                                periodEndDate={futurePeriodEndDate}
                                title="Options Chain Volume Analysis — Next 3 Months"
                                strikeHighlights={nextPeriodHighlights}
                                prediction={pastBasedPrediction}
                                expirationDates={expirationDates}
                                selectedExpirationDate={selectedExpirationDate}
                                onExpirationDateChange={onExpirationDateChange}
                                mode="forward"
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};